import { useQuery } from "@tanstack/react-query";

import {
  IncidentDetailParams,
  getIncident,
} from "../../services/productPlaneApi";
import { IncidentDetail } from "../../types/incidentDetail";
import { queryKeys } from "./keys";
import { planeErrorMessage } from "./planeError";

// Sources asked for with an incident: the most the backend gives in one
// page. The source table pages them itself.
export const INCIDENT_SOURCES_LIMIT = 200;

const INCIDENT_PARAMS: IncidentDetailParams = {
  sources_limit: INCIDENT_SOURCES_LIMIT,
};

interface UseIncidentResult {
  incident: IncidentDetail | null;
  loading: boolean;
  // Why the incident cannot be shown; null while it can, or while it loads
  error: string | null;
}

/**
 * TanStack Query hook for one incident.
 *
 * @param incidentId - The id of the incident; null fetches nothing
 * @returns The incident, the loading state and a message for a failed read
 */
export function useIncident(incidentId: string | null): UseIncidentResult {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.productPlane.incident(incidentId!, {
      ...INCIDENT_PARAMS,
    }),
    queryFn: () => getIncident(incidentId!, INCIDENT_PARAMS),
    enabled: !!incidentId,
    staleTime: 2 * 60 * 1000,
  });

  return {
    incident: data ?? null,
    loading: isLoading,
    error: error ? planeErrorMessage(error, "Incident") : null,
  };
}
